import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DarkModeProvider } from './context/DarkModeContext';

import Search      from './pages/Search';
import Upload      from './pages/Upload';
import Login       from './pages/Login';
import Signup      from './pages/Signup';
import Subscribe   from './pages/Subscribe';
import TrialSignup from './pages/TrialSignup';
import Account     from './pages/Account';
import Admin       from './pages/Admin';
import Favorites   from './pages/Favorites';
import Terms       from './pages/Terms';
import About       from './pages/About';
import FAQ         from './pages/FAQ';
import MyFacility    from './pages/MyFacility';
import WhyDifferent  from './pages/WhyDifferent';
import Alerts          from './pages/Alerts';
import ChangePassword  from './pages/ChangePassword';
import Housing         from './pages/Housing';
import WhySubscribe    from './pages/WhySubscribe';
import SecureInbox    from './pages/SecureInbox';
import ApiDocs        from './pages/ApiDocs';
import Moud           from './pages/Moud';

// Redirects to /login if user is not authenticated
function RequireAuth({ children }) {
  const { isAuthenticated, ready } = useAuth();
  if (!ready) return null; // wait for localStorage restore before redirecting
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

// Redirects to / if user is not admin
function RequireAdmin({ children }) {
  const { isAdmin, ready } = useAuth();
  if (!ready) return null;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/"        element={<Search />} />
      <Route path="/housing" element={<Housing />} />
      <Route path="/moud"    element={<Moud />} />
      <Route path="/login"   element={<Login />} />
      <Route path="/signup"  element={<Signup />} />
      <Route path="/trial"   element={<TrialSignup />} />
      <Route path="/terms"   element={<Terms />} />
      <Route path="/about"   element={<About />} />
      <Route path="/faq"     element={<FAQ />} />
      <Route path="/why-subscribe" element={<WhySubscribe />} />
      <Route path="/why"     element={<WhyDifferent />} />
      <Route path="/api-docs" element={<ApiDocs />} />

      {/* Authenticated */}
      <Route path="/subscribe" element={<Subscribe />} />
      <Route path="/account" element={
        <RequireAuth><Account /></RequireAuth>
      } />
      <Route path="/favorites" element={
        <RequireAuth><Favorites /></RequireAuth>
      } />
      <Route path="/my-facility" element={
        <RequireAuth><MyFacility /></RequireAuth>
      } />
      <Route path="/alerts" element={
        <RequireAuth><Alerts /></RequireAuth>
      } />
      <Route path="/change-password" element={
        <RequireAuth><ChangePassword /></RequireAuth>
      } />
      <Route path="/inbox" element={
        <RequireAuth><SecureInbox /></RequireAuth>
      } />

      {/* Admin only */}
      <Route path="/upload" element={
        <RequireAdmin><Upload /></RequireAdmin>
      } />
      <Route path="/admin" element={
        <RequireAdmin><Admin /></RequireAdmin>
      } />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <DarkModeProvider>
      <HelmetProvider>
        <AuthProvider>
          <Router>
            <AppRoutes />
          </Router>
        </AuthProvider>
      </HelmetProvider>
    </DarkModeProvider>
  );
}
